import { useQuery } from "@apollo/client";
import { GET_INTERVIEW_SESSIONS_WITH_PROFILES } from "../gql";
import type {
  ConversationsWithProfile,
  ConversationsWithProfileFilter,
  ConversationsWithProfilesOrderBy,
} from "@casey/db-types";

interface ConversationsWithProfilesData {
  conversationsWithProfiles: {
    edges: Array<{
      cursor: string;
      node: ConversationsWithProfile;
    }>;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
    totalCount: number;
  };
}

interface ConversationsWithProfilesVariables {
  first?: number;
  after?: string;
  offset?: number;
  filter?: ConversationsWithProfileFilter;
  orderBy?: ConversationsWithProfilesOrderBy[];
}

interface UseConversationsWithProfilesResult {
  sessions: ConversationsWithProfile[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  totalCount: number;
}

export function useConversationsWithProfiles(
  organizationId: string,
  options: {
    first?: number;
    page?: number;
    after?: string;
    filter?: ConversationsWithProfileFilter;
    orderBy?: ConversationsWithProfilesOrderBy[];
    skip?: boolean;
  } = {},
): UseConversationsWithProfilesResult {
  // Build the filter to include organization filtering
  const filter = {
    ...options.filter,
    ...(organizationId && { organizationId: { equalTo: organizationId } }),
  };

  // Calculate offset from page number (page 1 = offset 0, page 2 = offset pageSize, etc.)
  const pageSize = options.first || 20;
  const page = options.page || 1;
  const offset = (page - 1) * pageSize;

  const { data, loading, error, refetch, fetchMore } = useQuery<
    ConversationsWithProfilesData,
    ConversationsWithProfilesVariables
  >(GET_INTERVIEW_SESSIONS_WITH_PROFILES, {
    variables: {
      first: pageSize,
      offset,
      after: options.after,
      filter,
      orderBy: options.orderBy,
    },
    skip: options.skip || !organizationId,
    errorPolicy: "all",
  });

  // Get sessions directly from the view and deserialize JSONB fields
  const sessions =
    data?.conversationsWithProfiles?.edges?.map((edge) => {
      const session = edge.node;

      // Deserialize sessionProperties if it's a string (JSONB from database)
      if (
        session.sessionProperties &&
        typeof session.sessionProperties === "string"
      ) {
        try {
          return {
            ...session,
            sessionProperties: JSON.parse(session.sessionProperties as string),
          };
        } catch (e) {
          console.error("Failed to parse sessionProperties:", e);
          return session;
        }
      }

      return session;
    }) || [];

  // Get pagination info
  const pageInfo = data?.conversationsWithProfiles?.pageInfo;
  const totalCount = data?.conversationsWithProfiles?.totalCount || 0;

  const loadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;

    await fetchMore({
      variables: {
        after: pageInfo.endCursor,
      },
    });
  };

  return {
    sessions,
    loading,
    error: error?.message || null,
    refetch: async () => {
      await refetch();
    },
    loadMore,
    hasMore: pageInfo?.hasNextPage || false,
    totalCount,
  };
}
